import { CreatorOrderV2ContractError } from '@/apis/creatorOrderV2/errors';
import type {
  CreatorOrderChannelDto,
  CreatorOrderDetailV2,
  CreatorOrderRecordV2,
  CreatorProductOrderV2
} from '@/interface/creatorOrderV2';

const isRecord = (value: unknown): value is CreatorOrderRecordV2 =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getProductOrderId = (order: CreatorOrderRecordV2) => {
  const value = order.orderId ?? order.id ?? order._id;
  return value === undefined || value === null ? '' : String(value);
};

const normalizeChannel = (
  channel: CreatorOrderRecordV2,
  index: number
): CreatorOrderChannelDto => {
  const channelKey = String(channel.channelKey ?? '').trim();
  const platform = String(channel.platform ?? '')
    .trim()
    .toLowerCase();
  const platformId = String(channel.platformId ?? '').trim();

  if (!channelKey || !platform || !platformId) {
    throw new CreatorOrderV2ContractError(
      `Invalid V2 order channel at index ${index}: channelKey, platform and platformId are required.`
    );
  }
  if (typeof channel.active !== 'boolean') {
    throw new CreatorOrderV2ContractError(
      `Invalid V2 order channel ${channelKey}: active must be a boolean.`
    );
  }
  if (
    !Number.isSafeInteger(channel.deliveryRevision) ||
    channel.deliveryRevision < 0
  ) {
    throw new CreatorOrderV2ContractError(
      `Invalid V2 order channel ${channelKey}: deliveryRevision is required.`
    );
  }

  return {
    ...channel,
    channelKey,
    platform: platform as CreatorOrderChannelDto['platform'],
    platformId,
    active: channel.active,
    deliveryRevision: Number(channel.deliveryRevision)
  } as CreatorOrderChannelDto;
};

export const adaptCreatorProductOrderV2 = <T extends CreatorOrderRecordV2>(
  order: T
): T & CreatorProductOrderV2 => {
  const orderId = getProductOrderId(order);
  if (!orderId) {
    throw new CreatorOrderV2ContractError(
      'Invalid V2 product order: product orderId is required.'
    );
  }
  if (!Array.isArray(order.channels) || order.channels.length === 0) {
    throw new CreatorOrderV2ContractError(
      `Invalid V2 product order ${orderId}: channels[] must be non-empty.`
    );
  }

  const channels = order.channels.map(normalizeChannel);
  const uniqueChannelKeys = new Set(
    channels.map((channel) => channel.channelKey)
  );
  if (uniqueChannelKeys.size !== channels.length) {
    throw new CreatorOrderV2ContractError(
      `Invalid V2 product order ${orderId}: channelKey values must be unique.`
    );
  }

  return {
    ...order,
    id: orderId,
    _id: orderId,
    orderId,
    channels,
    quoteCapabilities: order.quoteCapabilities ?? {
      canAddChannels: false,
      canRemoveChannels: false
    }
  } as T & CreatorProductOrderV2;
};

export type AdaptedCreatorOrderDetailV2<T extends CreatorOrderDetailV2> = Omit<
  T,
  'order'
> & {
  order: T['order'] & CreatorProductOrderV2;
};

export const adaptCreatorOrderDetailV2 = <T extends CreatorOrderDetailV2>(
  detail: T
): AdaptedCreatorOrderDetailV2<T> => {
  if (!isRecord(detail) || !isRecord(detail.order)) {
    throw new CreatorOrderV2ContractError(
      'Invalid V2 creator detail: order is required.'
    );
  }
  const displayChannelKey =
    detail.displayChannelKey ?? detail.order.displayChannelKey;
  const sourceChannels = detail.channels ?? detail.order.channels;
  const audienceViews = Array.isArray(detail.audienceViews)
    ? detail.audienceViews
    : null;
  // The top-level audience belongs only to the server-selected representative.
  // Never overwrite channel data (including a processing state) or infer identity
  // from array position/platform when displayChannelKey is missing.
  const channels = Array.isArray(sourceChannels)
    ? sourceChannels.map((channel) =>
        audienceViews
          ? {
              ...channel,
              audienceView:
                audienceViews.find(
                  (view) => view.channelKey === channel.channelKey
                ) ?? null
            }
          : channel.channelKey === displayChannelKey &&
              channel.active !== false &&
              channel.audienceView == null &&
              channel.analysis?.audienceView == null &&
              isRecord(detail.audienceView)
            ? { ...channel, audienceView: detail.audienceView }
            : channel
      )
    : sourceChannels;
  const singleChannelAudience =
    audienceViews && Array.isArray(channels) && channels.length === 1
      ? audienceViews.find((view) => view.channelKey === channels[0].channelKey)
      : undefined;
  return {
    ...detail,
    ...(singleChannelAudience ? { audienceView: singleChannelAudience } : {}),
    ...(Array.isArray(detail.channels) ? { channels } : {}),
    order: adaptCreatorProductOrderV2({
      ...detail.order,
      // Product metadata is returned alongside the V2 order, while the
      // delivery flow is built from the normalized order object.
      product: detail.product ?? detail.order.product,
      campaign: detail.campaign ?? detail.order.campaign,
      quoteRevision: detail.quoteRevision ?? detail.order.quoteRevision,
      orderRole: detail.orderRole ?? detail.order.orderRole,
      audienceUploadIdentity: detail.influencer
        ? {
            platform: detail.order.platform,
            platformId: detail.influencer.platformId,
            influencerId: detail.influencer.influencerId
          }
        : undefined,
      channels,
      displayChannelKey,
      quoteCapabilities:
        detail.quoteCapabilities ?? detail.order.quoteCapabilities
    })
  } as AdaptedCreatorOrderDetailV2<T>;
};

const adaptNestedOrders = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(adaptNestedOrders);
  if (!isRecord(value)) return value;

  const next = Object.fromEntries(
    Object.entries(value).map(([key, child]) => [key, adaptNestedOrders(child)])
  );
  if (Array.isArray(next.channels) && getProductOrderId(next)) {
    return adaptCreatorProductOrderV2(next);
  }
  return next;
};

export const adaptCreatorOrderPayloadV2 = <T>(payload: T): T =>
  adaptNestedOrders(payload) as T;

export const adaptCreatorDashboardPayloadV2 = <T extends CreatorOrderRecordV2>(
  payload: T
): T => {
  if (!isRecord(payload)) return adaptCreatorOrderPayloadV2(payload);

  const preserveBalanceItemId = (item: unknown) =>
    isRecord(item)
      ? {
          ...item,
          // Get-paid ids identify balance items, not orders. Preserve them
          // before order normalization replaces id with orderId.
          balanceItemId: Object.prototype.hasOwnProperty.call(
            item,
            'balanceItemId'
          )
            ? item.balanceItemId
            : (item.id ?? null)
        }
      : item;
  const preserveGroup = (group: unknown): unknown => {
    if (Array.isArray(group)) return group.map(preserveBalanceItemId);
    if (isRecord(group) && Array.isArray(group.items)) {
      return { ...group, items: group.items.map(preserveBalanceItemId) };
    }
    return group;
  };
  const actions = Array.isArray(payload.actions)
    ? payload.actions.map((group) =>
        group?.group === 'get_paid' ? preserveGroup(group) : group
      )
    : isRecord(payload.actions)
      ? {
          ...payload.actions,
          ...(Object.prototype.hasOwnProperty.call(payload.actions, 'get_paid')
            ? { get_paid: preserveGroup(payload.actions.get_paid) }
            : {})
        }
      : payload.actions;

  return adaptCreatorOrderPayloadV2({ ...payload, actions });
};
